import {
  BoundSelectField,
  Button,
  Css,
  HelperText,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SubmitButton,
  useComputed,
  useModal,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useCallback, useEffect, useState } from "react";
import { LocationPathField } from "src/components/locationPathField/LocationPathField";
import { SelectMaybeNewField } from "src/components/selectMaybeNew/SelectMaybeNewField";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { TakeoffsStoreState } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { AddEditItemModal_ItemFragment } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemModal.generated";
import { useAddEditPlaceholderItemModal_GetItemsLazyQuery } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditPlaceholderItemModal.generated";
import { StoreApi } from "zustand";
import {
  AddEditItemModalDataViewProps,
  AddEditItemObjectState,
  CommonFields,
  PublishedTakeoffBanner,
  SelectedTask,
  TakeoffTaskBoundSelectField,
  TliData,
  saveAddEditItem,
  useAddEditItemModalFormState,
  withGetTliQuery,
} from "./utils";

export const AddEditPlaceholderItemModal = withGetTliQuery(AddEditPlaceholderItemModalDataView);

function AddEditPlaceholderItemModalDataView(props: AddEditItemModalDataViewProps) {
  const { planPackageId, initialTliData, takeoffsManagerStore } = props;

  const { saveTli, planPackage } = takeoffsManagerStore.getState();
  const { closeModal, addCanClose } = useModal();
  const isEditing = !!initialTliData?.id;
  const readOnly = !planPackage.canEditLineItems.allowed;

  const formState = useAddEditItemModalFormState("placeholder", readOnly, initialTliData);

  addCanClose(() => {
    return formState.dirty ? window.confirm("You have unsaved changes. Are you sure you want to close?") : true;
  });

  return (
    <>
      <ModalHeader>
        <span css={Css.lg.$}>
          {readOnly ? "Placeholder Details" : isEditing ? "Edit Placeholder" : "Add Placeholder"}
        </span>
      </ModalHeader>
      <ModalBody>
        {readOnly && <PublishedTakeoffBanner />}
        <div css={Css.df.fdc.gap2.pb3.$}>
          <PlaceholderFields
            store={takeoffsManagerStore}
            planPackageId={planPackageId}
            initialTliData={initialTliData}
            readOnly={readOnly}
            formState={formState}
            isEditing={isEditing}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        {readOnly ? (
          <Button label="Close" onClick={closeModal} variant="primary" />
        ) : (
          <>
            <Button variant="quaternary" label="Cancel" onClick={closeModal} size="lg" />
            <SubmitButton
              label="Save"
              data-testid="saveBtn"
              form={formState}
              onClick={async () => {
                await saveAddEditItem({ formState, saveTli, isPlaceholderItem: true });
                closeModal();
              }}
              size="lg"
            />
          </>
        )}
      </ModalFooter>
    </>
  );
}

type PlaceholderFieldsProps = {
  store: StoreApi<TakeoffsStoreState>;
  planPackageId: string;
  initialTliData?: TliData;
  readOnly: boolean;
  formState: ObjectState<AddEditItemObjectState>;
  isEditing: boolean;
};

function PlaceholderFields(props: PlaceholderFieldsProps) {
  const { store, planPackageId, initialTliData, readOnly, formState, isEditing } = props;
  const [selectedTask, setSelectedTask] = useState<SelectedTask | undefined | null>(
    initialTliData?.task ? { id: initialTliData?.task?.id, name: initialTliData?.task?.name } : undefined,
  );
  const setDefaults = useCallback(
    (selectedItem: AddEditItemObjectState["item"] | undefined) => {
      // Default the item UOM, even if a value had been specified
      if (selectedItem?.unitOfMeasure?.id) {
        formState.unitOfMeasureId.set(selectedItem?.unitOfMeasure?.id);
        formState.unitOfMeasure.set(selectedItem?.unitOfMeasure);
      }
      // Default the item task
      if (formState.locationId.value && !formState.taskId.value && selectedItem?.planTaskMappings?.nonEmpty) {
        const taskMappings = selectedItem?.planTaskMappings;
        const exactTaskMatch = taskMappings.find((ptm) => ptm.location?.id === formState.locationId.value);
        const wildCardLocationMatch = taskMappings.find((ptm) => !ptm.location);
        const defaultTask = exactTaskMatch ?? wildCardLocationMatch;
        if (defaultTask) {
          formState.taskId.set(defaultTask?.task.id);
          setSelectedTask(defaultTask?.task);
        }
      }
    },
    [formState],
  );

  const onItemSelect = useCallback(
    (id: string | undefined, selectedItem: AddEditItemObjectState["item"] | undefined) => {
      // Set the material variant on the line
      formState.itemId.set(id);
      formState.item.set(selectedItem);
      setDefaults(selectedItem);
    },
    [formState, setDefaults],
  );

  const itemOptions = useLazyItems(readOnly);
  const { dimensions, currentMavs } = useComputed(() => {
    return {
      dimensions: formState.item.materialAttributeDimensions?.rows?.filter((mad) =>
        // use effectiveMADs when editing placeholder
        isEditing
          ? (formState.materialVariant.value?.effectiveMADs?.some((effMad) => effMad.id === mad.id.value) ?? false)
          : mad.useInTakeoff.value,
      ),
      currentMavs: formState.materialVariant.value?.materialAttributeValues ?? [],
    };
  }, [formState]);

  return (
    <>
      <BoundSelectField
        label="Placeholder"
        field={formState.itemId}
        nothingSelectedText="Please select a material"
        options={itemOptions}
        getOptionLabel={(o) => o.displayName}
        getOptionValue={(o) => o.id}
        placeholder="Select material"
        onSelect={onItemSelect}
        disabled={disableBasedOnPotentialOperation(initialTliData?.canEditMaterial)}
      />
      {formState.itemId.value && dimensions.nonEmpty && (
        <div css={Css.df.fdc.gap1.$}>
          <div css={Css.dg.gtc("30% 30% 30%").gap2.aie.$}>
            {dimensions.map((dimensionState) => {
              const mad = dimensionState.value;
              const unitLabel = mad.unitOfMeasure
                ? ` (${mad.unitOfMeasure?.abbreviation || mad.unitOfMeasure?.name})`
                : "";
              const valueOption = currentMavs.find(({ dimension }) => dimension.id === mad.id);
              const value = valueOption?.id;

              return (
                <div css={Css.df.$} key={mad.id}>
                  <SelectMaybeNewField
                    label={`${mad.name}${unitLabel}`}
                    // We may have historical MAVs/MADs no longer used in takeoff,
                    // but we still want to display them as read-only during editing.
                    readOnly={!mad.useInTakeoff}
                    onAdd={(newValue) => {
                      const newEntry = { dimension: mad, textValue: newValue, id: "" };
                      dimensionState.set({ ...mad, values: [...dimensionState.values.value, newEntry] });
                      formState.materialVariant.materialAttributeValues.set([
                        ...currentMavs.filter(({ dimension }) => dimension.id !== mad.id),
                        newEntry,
                      ]);
                    }}
                    unsetLabel="-"
                    onSelect={(mavId) => {
                      const otherMavs = currentMavs.filter((mav) => mav.dimension.id !== mad.id);
                      if (!mavId) {
                        // Clear this dimension's value
                        formState.materialVariant.materialAttributeValues.set(otherMavs);
                      } else {
                        const newValue = mad.values?.find((v) => v.id === mavId);
                        if (newValue) {
                          formState.materialVariant.materialAttributeValues.set([...otherMavs, newValue]);
                        }
                      }
                    }}
                    options={mad.values?.map((mav) => ({ id: mav.id, name: mav.textValue }))}
                    // NOTE: This field might need to be updated to handle range values
                    //    SC-55230
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    value={value}
                  />
                </div>
              );
            })}
          </div>
          <HelperText
            helperText={<div>Note: You can specify numeric ranges like '&lt; 10', '&gt; 10' or '10-20'</div>}
          />
        </div>
      )}
      <LocationPathField
        filter={{ rpav: [store.getState().planPackage.version.id] }}
        newLocationPathField={formState.newLocationPath}
        field={formState.locationId}
        onSelect={(_locationId, location) => {
          formState.location.set(location);
          formState.item.value && setDefaults(formState.item.value);
        }}
        disabled={disableBasedOnPotentialOperation(initialTliData?.canEditLocation)}
      />
      <TakeoffTaskBoundSelectField
        allowNone={true}
        selectedTask={selectedTask}
        readOnly={readOnly}
        taskId={formState.taskId}
      />
      <CommonFields
        planPackageId={planPackageId}
        planPackageVersionId={store.getState().planPackage.version.id}
        readOnly={readOnly}
        formState={formState}
        disabled={{
          options: disableBasedOnPotentialOperation(initialTliData?.canEditOptions),
          quantity: disableBasedOnPotentialOperation(initialTliData?.canEditQuantity),
        }}
      />
    </>
  );
}

// Loads all items, page by page...
function useLazyItems(readOnly: boolean) {
  const [loadItems] = useAddEditPlaceholderItemModal_GetItemsLazyQuery({
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-only",
  });

  const [itemOptions, setItemOptions] = useState<AddEditItemModal_ItemFragment[]>([]);

  // load all material variants, page by page
  useEffect(() => {
    if (readOnly) return;
    void (async () => {
      const response = await loadItems();
      if (response.data?.items) {
        // Make a copy of the MAD to remove the Object.freeze from Apollo. Since SelectMaybeNewField appends a newly-added MAD to our list
        setItemOptions(
          response.data!.items.map(({ materialAttributeDimensions, ...rest }) => ({
            ...rest,
            materialAttributeDimensions: materialAttributeDimensions?.map(({ ...rest }) => ({ ...rest })),
          })),
        );
      }
    })();
  }, [loadItems, readOnly]);
  return itemOptions;
}
